import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const ACCENT = '#60A5FA';
const GOLD = '#D6EF4E';

const PendingBadge = () => (
  <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg"
    style={{ background: 'rgba(251,191,36,0.08)', border: '1px solid rgba(251,191,36,0.20)' }}>
    <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
    <span className="font-oranienbaum text-amber-400 text-xs tracking-widest uppercase">Pendiente de conexión con Supabase</span>
  </div>
);

const kpis = [
  { label: 'Facturación Hoy', value: '—', icon: 'TrendingUp', color: ACCENT, sub: 'Sin datos' },
  { label: 'Facturación Mes', value: '—', icon: 'BarChart2', color: GOLD, sub: 'Sin datos' },
  { label: 'Beneficio Bruto', value: '—', icon: 'DollarSign', color: '#34D399', sub: 'Sin datos' },
  { label: 'Margen Medio', value: '—', icon: 'Percent', color: '#A78BFA', sub: 'Sin datos' },
  { label: 'Pedidos Pendientes', value: '—', icon: 'Clock', color: '#FBBF24', sub: 'Sin datos' },
  { label: 'Pedidos Entregados', value: '—', icon: 'CheckCircle', color: '#4ADE80', sub: 'Sin datos' },
  { label: 'Cobros Pendientes', value: '—', icon: 'ArrowDownCircle', color: '#F87171', sub: 'Sin datos' },
  { label: 'Pagos Pendientes', value: '—', icon: 'ArrowUpCircle', color: '#FB923C', sub: 'Sin datos' },
  { label: 'Clientes Activos', value: '—', icon: 'UserCircle', color: '#38BDF8', sub: 'Sin datos' },
  { label: 'Stock Valorizado', value: '—', icon: 'Package', color: '#818CF8', sub: 'Sin datos' },
  { label: 'Bajo Mínimo', value: '—', icon: 'AlertTriangle', color: '#FBBF24', sub: 'Sin datos' },
  { label: 'Ticket Medio', value: '—', icon: 'ShoppingCart', color: '#F472B6', sub: 'Sin datos' },
];

const dateFilters = ['Hoy', 'Ayer', 'Semana', 'Mes', 'Trimestre', 'Año', 'Personalizado'];

const emptyEvolucion = Array.from({ length: 7 }, (_, i) => ({ day: `D${i + 1}`, facturacion: 0, beneficio: 0 }));
const emptyClientes = [
  { name: 'Cliente A', beneficio: 0 },
  { name: 'Cliente B', beneficio: 0 },
  { name: 'Cliente C', beneficio: 0 },
  { name: 'Cliente D', beneficio: 0 },
];
const emptyEstados = [
  { name: 'Pendiente', value: 0 },
  { name: 'En Reparto', value: 0 },
  { name: 'Entregado', value: 0 },
  { name: 'Incidencia', value: 0 },
];
const COLORS = [ACCENT, GOLD, '#34D399', '#F87171'];

const operativeLists = [
  { title: 'Pedidos Pendientes de Preparar', icon: 'ClipboardList', color: ACCENT, items: [] },
  { title: 'Entregas Previstas Hoy', icon: 'Truck', color: '#34D399', items: [] },
  { title: 'Facturas Vencidas', icon: 'AlertCircle', color: '#F87171', items: [] },
  { title: 'Clientes con Deuda', icon: 'UserX', color: '#FBBF24', items: [] },
  { title: 'Productos Bajo Stock Mínimo', icon: 'AlertTriangle', color: '#FB923C', items: [] },
  { title: 'Incidencias de Reparto', icon: 'XOctagon', color: '#F472B6', items: [] },
];

const DashboardDistribucion = () => {
  const [dateFilter, setDateFilter] = useState('Mes');

  return (
    <div className="min-h-screen px-4 py-6 md:px-8"
      style={{ background: 'linear-gradient(135deg, #0A0A0A 0%, #111111 50%, #0D0D0D 100%)' }}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <nav className="flex items-center gap-2 text-xs font-oranienbaum text-white/30 mb-2">
            <span>🚚 Distribución</span>
            <Icon name="ChevronRight" size={12} />
            <span className="text-white/60">Dashboard</span>
          </nav>
          <div className="flex items-center gap-3 mb-1">
            <span className="text-2xl">🚚</span>
            <h1 className="font-oranienbaum font-bold text-white text-2xl md:text-3xl tracking-wide">Dashboard Distribución</h1>
          </div>
          <p className="font-oranienbaum text-white/40 text-sm ml-10">Vista ejecutiva · Unidad de Negocio B2B</p>
        </div>
        <div className="flex flex-col items-end gap-2">
          <PendingBadge />
          <div className="flex gap-2">
            <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-oranienbaum text-white/50 hover:text-white/80 transition-colors"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
              <Icon name="Download" size={13} />
              Exportar
            </button>
          </div>
        </div>
      </div>
      {/* Date Filters */}
      <div className="flex flex-wrap gap-2 mb-6">
        <div className="flex items-center gap-1 p-1 rounded-xl flex-wrap"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          {dateFilters?.map(f => (
            <button key={f} onClick={() => setDateFilter(f)}
              className="px-3 py-1.5 rounded-lg text-xs font-oranienbaum transition-all"
              style={dateFilter === f
                ? { background: `${ACCENT}20`, color: ACCENT, border: `1px solid ${ACCENT}40` }
                : { color: 'rgba(255,255,255,0.40)', border: '1px solid transparent' }}>
              {f}
            </button>
          ))}
        </div>
        <button className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-oranienbaum text-white/50 hover:text-white/70 transition-colors"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
          <Icon name="Filter" size={13} />
          Más filtros
        </button>
      </div>
      {/* KPIs Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 mb-8">
        {kpis?.map((kpi, i) => (
          <div key={i} className="rounded-xl p-4 flex flex-col gap-2"
            style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center justify-between">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center"
                style={{ background: `${kpi?.color}15`, border: `1px solid ${kpi?.color}25` }}>
                <Icon name={kpi?.icon} size={15} style={{ color: kpi?.color }} />
              </div>
            </div>
            <p className="font-oranienbaum font-bold text-white text-xl">{kpi?.value}</p>
            <div>
              <p className="font-oranienbaum text-white/55 text-[10px] leading-tight">{kpi?.label}</p>
              <p className="font-oranienbaum text-white/25 text-[9px] mt-0.5">{kpi?.sub}</p>
            </div>
          </div>
        ))}
      </div>
      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-4">
        {/* Evolución Facturación */}
        <div className="lg:col-span-2 rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-oranienbaum font-semibold text-white text-sm">Evolución de Facturación</h3>
              <p className="font-oranienbaum text-white/35 text-xs mt-0.5">Facturación y beneficio por período</p>
            </div>
            <div className="flex items-center gap-3 text-xs font-oranienbaum">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: ACCENT }} />Facturación</span>
              <span className="flex items-center gap-1 text-white/40"><span className="w-2 h-2 rounded-full bg-[#34D399]" />Beneficio</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={emptyEvolucion}>
              <defs>
                <linearGradient id="distFact" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={ACCENT} stopOpacity={0.15} />
                  <stop offset="95%" stopColor={ACCENT} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="distBen" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#34D399" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#34D399" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="day" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10, fontFamily: 'oranienbaum' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10, fontFamily: 'oranienbaum' }} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontFamily: 'oranienbaum', fontSize: 12 }} />
              <Area type="monotone" dataKey="facturacion" stroke={ACCENT} strokeWidth={2} fill="url(#distFact)" />
              <Area type="monotone" dataKey="beneficio" stroke="#34D399" strokeWidth={2} fill="url(#distBen)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        {/* Pedidos por Estado */}
        <div className="rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-semibold text-white text-sm mb-1">Pedidos por Estado</h3>
          <p className="font-oranienbaum text-white/35 text-xs mb-4">Distribución actual</p>
          <ResponsiveContainer width="100%" height={140}>
            <PieChart>
              <Pie data={emptyEstados} cx="50%" cy="50%" innerRadius={40} outerRadius={65} dataKey="value" paddingAngle={3}>
                {emptyEstados?.map((_, idx) => (
                  <Cell key={idx} fill={COLORS?.[idx % COLORS?.length]} opacity={0.7} />
                ))}
              </Pie>
              <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontFamily: 'oranienbaum', fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="grid grid-cols-2 gap-1 mt-2">
            {emptyEstados?.map((e, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: COLORS?.[i % COLORS?.length] }} />
                <span className="font-oranienbaum text-white/50 text-[10px] truncate">{e?.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-8">
        {/* Beneficio por Cliente */}
        <div className="rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-semibold text-white text-sm mb-1">Beneficio por Cliente</h3>
          <p className="font-oranienbaum text-white/35 text-xs mb-4">Top clientes por rentabilidad</p>
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={emptyClientes} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" horizontal={false} />
              <XAxis type="number" tick={{ fill: 'rgba(255,255,255,0.35)', fontSize: 10, fontFamily: 'oranienbaum' }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="name" tick={{ fill: 'rgba(255,255,255,0.50)', fontSize: 10, fontFamily: 'oranienbaum' }} axisLine={false} tickLine={false} width={70} />
              <Tooltip contentStyle={{ background: '#1A1A1A', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, fontFamily: 'oranienbaum', fontSize: 12 }} />
              <Bar dataKey="beneficio" fill={ACCENT} radius={[0, 4, 4, 0]} opacity={0.8} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Cobros Vencidos */}
        <div className="rounded-2xl p-5"
          style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
          <h3 className="font-oranienbaum font-semibold text-white text-sm mb-1">Antigüedad de Cobros</h3>
          <p className="font-oranienbaum text-white/35 text-xs mb-4">Clasificación por vencimiento</p>
          <div className="space-y-3">
            {[
              { label: 'No vencido', color: '#34D399', pct: 0 },
              { label: '1–30 días', color: GOLD, pct: 0 },
              { label: '31–60 días', color: '#FB923C', pct: 0 },
              { label: '61–90 días', color: '#F87171', pct: 0 },
              { label: '+90 días', color: '#DC2626', pct: 0 },
            ]?.map((item, i) => (
              <div key={i}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-oranienbaum text-white/60 text-xs">{item?.label}</span>
                  <span className="font-oranienbaum text-white/40 text-xs">— €</span>
                </div>
                <div className="h-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)' }}>
                  <div className="h-full rounded-full transition-all" style={{ width: `${item?.pct}%`, background: item?.color }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Operational Lists */}
      <div className="mb-4">
        <h2 className="font-oranienbaum font-semibold text-white/70 text-sm uppercase tracking-widest mb-4">Listas Operativas</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
          {operativeLists?.map((list, i) => (
            <div key={i} className="rounded-2xl p-5"
              style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="flex items-center gap-2 mb-4">
                <div className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ background: `${list?.color}15`, border: `1px solid ${list?.color}25` }}>
                  <Icon name={list?.icon} size={14} style={{ color: list?.color }} />
                </div>
                <h3 className="font-oranienbaum font-semibold text-white text-xs">{list?.title}</h3>
              </div>
              <div className="flex flex-col items-center justify-center py-6 gap-2">
                <div className="w-8 h-8 rounded-full flex items-center justify-center"
                  style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                  <Icon name="Database" size={14} className="text-white/20" />
                </div>
                <p className="font-oranienbaum text-white/25 text-xs text-center">Sin datos · Pendiente Supabase</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default DashboardDistribucion;
